import {
  IAccountComment,
  IAnonymousUser,
  IUserDataForMemberManagement,
  IUserLeaderboardPreference,
  IUserLeaderboardPreferenceViewResult,
  IUserNames,
  NotFoundError,
  propNamesIUserDataForMemberManagement,
  propNamesIUserNames,
  WorkoutImportOrigin,
} from 'fitbeat.models';
import { UserProfile } from '../../../entities/user/userprofile';
import { IUserProfileRepository } from '../interfaces/iuserprofile-repository';
import { PouchDbQueryHelper } from './pouchdb-query-helper';

export class DbUserProfileRepository implements IUserProfileRepository {
  private db: PouchDbQueryHelper;

    constructor(private userSettingsDb: any) {
        this.db = new PouchDbQueryHelper(this.userSettingsDb, 'DbUserProfileRepository');
    }

    public async getUsersSignedUpAfter(requestTime: Date, signupAfter: Date, gymName: string): Promise<UserProfile[]> {
        const resultFromView = await this.db.queryView('user-search/signupdate-and-gym', {
            startkey: [signupAfter.toISOString(), gymName],
            endkey: [requestTime.toISOString(), gymName],
            include_docs: true,
        });

        const userDocs = resultFromView.map((value: any) => value.doc);
        const users = this.removeDuplicates(userDocs, 'uid');
        return this.mapDbDocsToUserProfiles(users);
    }

    public async getUserProfileOrThrow(uid: string): Promise<UserProfile> {
      const user = this.userSettingsDb.get(uid);
      if (!user) {
        throw new NotFoundError('user', uid);
      }

      return user;
    }

    public async add(profile: UserProfile): Promise<void> {
        const doc = JSON.parse(JSON.stringify(profile));
        doc._id = profile.uid;
        return this.userSettingsDb.put(doc);
    }

    public async update(profile: UserProfile): Promise<UserProfile> {
        const doc = JSON.parse(JSON.stringify(profile));
        await this.userSettingsDb.put(doc);
        return this.userSettingsDb.get(profile.uid);
    }

    public async get(uid: string): Promise<UserProfile | undefined> {
        try {
            const userProfileDbObject = await this.userSettingsDb.get(uid);
            userProfileDbObject.uid = userProfileDbObject._id;
            const userProfile =  UserProfile.createUserProfileFromDbObject(userProfileDbObject);
            await this.applyParentPlanDetails(userProfile);
            return userProfile;
        } catch (err) {
            if (err.status === 404) {
                return undefined;
            } else {
                throw err;
            }
        }
    }

  public async getByContinueSignupToken(token: string): Promise<UserProfile | undefined> {
    const queryOptions = {
      selector: {
        continueSignupToken: token,
      },
      limit: 1,
    };
    const docs = await this.db.queryAndGetDocs(`getByContinueSignupToken(${token})`, queryOptions);
    if (docs.length === 0) {
      return undefined;
    }

    const userProfile = UserProfile.createUserProfileFromDbObject(docs[0]);
    userProfile.uid = docs[0]._id;
    return userProfile;
  }

    /**
     * @deprecated Use getUsersByIds instead. If user ids are not knonw use cached repo
     */
    public async getAll(): Promise<UserProfile[]> {
        const results: any = await this.userSettingsDb.allDocs({ include_docs: true });
        const docs: any[] = results
            .rows
            .map((row: any) => row.doc)
            .filter((doc: any) => doc._id !== '0');
        const users: UserProfile[] = this.mapDbDocsToUserProfiles(docs);
        return users;
    }

    public async getByStudioBeltNumber(heartRateMonitorIdentifier: string): Promise<UserProfile[]> {
        // the _studio_ heartRateMonitorIdentifier is included in the user-search/user-search view
        return this.search(heartRateMonitorIdentifier);
    }

    public async search(searchQuery: string): Promise<UserProfile[]> {
        const resultFromView = await this.db.queryView('user-search/user-search', {
            start_key: searchQuery.trim().toLowerCase(),
            end_key: `${searchQuery.trim().toLowerCase()}\ufff0`,
            include_docs: true,
        });

        const userDocs = resultFromView.map((value: any) => value.doc);
        const users = this.removeDuplicates(userDocs, 'uid');
        return this.mapDbDocsToUserProfiles(users);
    }

    public async getUserByEmail(email: string): Promise<UserProfile | undefined> {
        const lowerCaseEmail = email.toLowerCase();
        const result = await this.userSettingsDb.find({
            selector: {
                email: lowerCaseEmail,
            },
            use_index: [
                '_design/userProfileEmailIndex',
                'userProfileEmailIndex',
            ],
        });
        const docs = result.docs;
        if (docs.length === 0) {
            return;
        }
        if (docs.length >= 2) {
            throw new Error(`multiple users linked with email ${email}`);
        }
        return docs[0];
    }

    public async getUserByWorkoutImportConfig(
        origin: WorkoutImportOrigin,
        userId: string,
    ): Promise<UserProfile> {
        const result = await this.userSettingsDb.query('user-search/external-workout-import-config', {
            key: [origin, userId],
        });
        if (result.rows.length === 0) {
            throw new NotFoundError('external user id', userId);
        }
        const user = await this.get(result.rows[0].id);
        if (!user) {
            throw NotFoundError.User(result.rows[0].id);
        }
        return user;
    };

    public async getUsersByEmails(emails: string[]): Promise<UserProfile[]> {
        const result = await this.userSettingsDb.find({
            selector: {
                email: {
                    $in: emails,
                },
            },
        });
        return result.docs;
    }

    public async getMemberInfoByGymName(gymName?: string, emailExclusionFilter?: string)
        : Promise<IUserDataForMemberManagement[]> {
        let selector;
        if (gymName !== undefined) {
            selector = {
                gymName,
            };
        } else {
            selector = {
                gymName: {
                    $exists: false,
                },
            };
        }

        if (emailExclusionFilter) {
            const emailExcludeSelector = {
                $not: {
                    email: {
                        $regex: this.regexExclusion(emailExclusionFilter),
                    },
                },
            };
            selector = {...selector, ...emailExcludeSelector};
        }
        const queryOptions = {
            selector,
            limit: 10000,
            fields: propNamesIUserDataForMemberManagement(),
            use_index: [
                '_design/userProfileGymNameIndex',
                 'userProfileGymNameIndex',
             ],
        };
        return await this.db.queryAndGetDocs(`getMemberInfoByGymName(${gymName})`, queryOptions);
    }

    public async getUsersByGymName(gymName?: string, emailExclusionFilter?: string): Promise<UserProfile[]> {
        let selector;
        if (gymName !== undefined) {
            selector = {
                gymName,
            };
        } else {
            selector = {
                gymName: {
                    $exists: false,
                },
            };
        }

        if (emailExclusionFilter) {
            const emailExcludeSelector = {
                $not: {
                    email: {
                        $regex: this.regexExclusion(emailExclusionFilter),
                    },
                },
            };
            selector = {...selector, ...emailExcludeSelector};
        }
        const queryOptions = {
            selector,
            limit: 10000,
            use_index: [
                '_design/userProfileGymNameIndex',
                 'userProfileGymNameIndex',
             ],
        };
        return await this.db.queryAndGetDocs(`getUsersByGymName(${gymName})`, queryOptions);
    }

    public async getUsersByUtmTagSource(source: string, medium: string): Promise<UserProfile[]> {
        const queryOptions = {
            selector: {
                influencerSettings: {
                    utmTags: {
                       medium,
                       source: {
                          $regex: `(?i)${source}`,
                       },
                    },
                 },
           },
           use_index: [
               '_design/influencer-settings',
                'medium',
            ],
        };
        return await this.db.queryAndGetDocs(`getUserByUtmTagSource(${source})`, queryOptions);
    }

    public async getUsersByFilters(
        emailExclusionFilter?: string, planIds?: string[], includeOldAccounts?: boolean): Promise<UserProfile[]> {

        let emailSelector;
        if (emailExclusionFilter) {
            emailSelector = {
                $not: {
                    email: {
                        $regex: this.regexExclusion(emailExclusionFilter),
                    },
                },
            };
        }
        let planIdSelector: any = {
            currentPlanId: {
                $in: planIds,
            },
        };
        if (includeOldAccounts) {
            planIdSelector = {
                $or: [
                    {
                        currentPlanId: {
                            $exists: false,
                        },
                    },
                    {
                        currentPlanId: {
                            $in: planIds,
                        },
                    },
                ],
            };
        }
        const queryOptions = {
            selector: {
                ...emailSelector,
                ...planIdSelector,
            },
            fields: [
                '_id',
                'uid',
                'fullname',
                'email',
                'mobileNumber',
                'userSignupDate',
                'currentPlanStatus',
                'currentPlanId',
                'timeSlotType',
                'planHistory',
                'dateOfBirth',
                'gender',
                'skillLevel',
                'longTermGoal',
                'stripeUserId',
                'continueSignupToken',
                'medium',
                'signupSource',
                'isStreamingUser',
                'influencerSettings',
                'leadStatus',
                'ptApprovedAt',
                'followUpDate',
                'strengthRatioForMuscleGroups',
            ],
            limit: 10000,
        };
        return await this.db.queryAndGetDocs(`getUsersByFilters(${emailExclusionFilter})`, queryOptions);
    }

    public async getUsersByIds(userprofileIds: string[]): Promise<UserProfile[]> {
        return await this.db.bulkGet(userprofileIds);
    }

  public async getPotentiallyDeletedUsersByIds(userprofileIds: string[]): Promise<(UserProfile | IAnonymousUser)[]> {
    const foundUsers = await this.db.bulkGet(userprofileIds);
    return userprofileIds.map((userId: string) => {
      const found = foundUsers.find((user) => user._id === userId);

      if (!found) {
        return {
          _id: userId,
          uid: userId,
          nickname: 'Anonymous',
        };
      }

      return found;
    });
  }

  public async getUserByEnrolledFaceId(enrolledFaceId: string): Promise<UserProfile | undefined> {
      const queryOptions = {
        selector: {
          enrolledFaceId,
        },
      };

      const docs = await this.db.queryAndGetDocs(`getUserByEnrolledFaceId()`, queryOptions);

      if (docs.length > 1) {
        throw new Error(`more than one user found with face id ${enrolledFaceId}`);
      }

      if (docs.length === 0) {
        return undefined;
      }

      return docs[0];
    }

    public async removeContinueSignupToken(userId: string): Promise<UserProfile> {
        const userProfile = await this.userSettingsDb.get(userId);
        delete userProfile.continueSignupToken;
        delete userProfile.continueSignupLink;
        return this.userSettingsDb.put(userProfile);
    }

    public async updateAccountComment(userId: string, accountComment: IAccountComment): Promise<IAccountComment[]> {
        const profile = await this.userSettingsDb.get(userId);
        const existingComments = profile.accountComments ? profile.accountComments : [];
        existingComments.push(accountComment);
        profile.accountComments = existingComments;
        const changedProfile = await this.update(profile);
        return changedProfile.accountComments ? changedProfile.accountComments : [];
    }

    public async updateFollowUpDate(userId: string, followUpDate: Date): Promise<Date | undefined> {
        const profile = await this.userSettingsDb.get(userId);
        if (!followUpDate) {
            delete profile.followUpDate;
            return;
        }
        profile.followUpDate = followUpDate;
        const changedProfile = await this.update(profile);
        return changedProfile.followUpDate ;
    }

    public async removeFollowUpDate(userId: string): Promise<void> {
        const profile = await this.userSettingsDb.get(userId);
        if (!profile) {
            throw new Error('User not found');
        }
        delete profile.followUpDate;
        await this.update(profile);
    }

    public async getFollowUpDate(userId: string): Promise<Date | undefined> {
        const profile = await this.userSettingsDb.get(userId);
        return profile.followUpDate;
    }

    public async getAllUsersInFamilyPlan(planOwnerUserProfileId: string): Promise<UserProfile[]> {
      const queryOptions = {
        selector: {
          $or: [{
            planOwnerUserProfileId,
          }, {
            _id: planOwnerUserProfileId,
          }],
        },
      };

      const result = await this.db.queryAndGetDocs(`getAllUsersInFamilyPlan()`, queryOptions);
      const familyMembers: UserProfile[] = [];

      result.forEach((userProfileFromDb: any) => {
        let userProfile: any = userProfileFromDb;
        userProfile.uid = userProfileFromDb._id;
        userProfile = UserProfile.createUserProfileFromDbObject(userProfileFromDb);
        familyMembers.push(userProfile as UserProfile);
      });

      return familyMembers;
    }

    public async getSingleUserForTesting(): Promise<any> {
      const queryOptions = {
        selector: {},
        limit: 1,
      };
      const result = await this.db.queryAndGetDocs(`getSingleUserForTesting()`, queryOptions);
      return result;
    }

  public async getAllUsersInTeam(team: string): Promise<IUserNames[]> {
    const queryOptions = {
      selector: {
        teams: {
          $in: [team],
        },
      },
      fields: propNamesIUserNames(),
      limit: 10000,
    };
    return await this.db.queryAndGetDocs(`getAllUsersInTeam()`, queryOptions);
  }

  public async getLeaderboardPreferencesForUsersByLeaderboardId(
    leaderboardId: string,
  ): Promise<IUserLeaderboardPreferenceViewResult[]> {
    const userLeaderboardPreferencesViewIndexes = {
      leaderboardId: 0,
      userProfileId: 1,
      leaderboardPreferences: 2,
    };

    const resultFromView = await this.db.queryView('user-leaderboard-preferences/leaderboard-preference', {
      start_key: [leaderboardId],
      end_key: [leaderboardId, {}],
      group_level: 4,
    });

    const mapLeaderboardPreferences = (leaderboardPreference: IUserLeaderboardPreference):
    IUserLeaderboardPreference => {
      return {
        optInHistory: leaderboardPreference.optInHistory.map((entry) => {
          return {
            ...entry,
            optOutTime: entry.optOutTime ? new Date(entry.optOutTime) : undefined,
            optInTime: entry.optInTime ? new Date(entry.optInTime) : undefined,
          };
        }),
      };
    };

    const mappedResult: IUserLeaderboardPreferenceViewResult[] = resultFromView.map((result: any) => {
      const res: IUserLeaderboardPreferenceViewResult = {
        leaderboardPreference:
          mapLeaderboardPreferences(result.key[userLeaderboardPreferencesViewIndexes.leaderboardPreferences]),
        userProfileId: result.key[userLeaderboardPreferencesViewIndexes.userProfileId],
      };

      return res;
    });

    return mappedResult;
  }

    private removeDuplicates<T>(myArr: T[], prop: string) {
        return myArr.filter((obj, pos, arr) => {
            return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos;
        });
    }

    private regexExclusion(substring: string) {
        const searchQueryEscapedForRegex = substring.split(',').map((item) => {
            return item ? escapeRegExp(item!) : ' ';
        }).join('|');
        function escapeRegExp(str: string): string {
            return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
        }
        // this is needed because Pouchdb-node reacts differently based on the adapter when it comes to regex
        const regex = RegExp(`${searchQueryEscapedForRegex}`, 'i');
        const regexString = `(?i)${searchQueryEscapedForRegex}`;
        const useRegexString = this.userSettingsDb.adapter === 'leveldb' || this.userSettingsDb.adapter === 'memory';
        return useRegexString ? regex : regexString;
    }

    private async applyParentPlanDetails(userProfile: UserProfile) {
        if (!userProfile.planOwnerUserProfileId) {
            return;
        }

        const planOwnerUserProfile = await this.get(userProfile.planOwnerUserProfileId);

        if (!planOwnerUserProfile) {
            throw new Error(`No parent profile found with id: ${userProfile.planOwnerUserProfileId}`);
        }

        userProfile.applyParentPlanDetails(planOwnerUserProfile);
    }

    private mapDbDocsToUserProfiles(dbDocs: any[]): UserProfile[] {
        return dbDocs.map((singleUser: any) => {
            singleUser = UserProfile.createUserProfileFromDbObject(singleUser);
            singleUser.uid = singleUser._id;
            return singleUser;
        });
    }
 }
